import React, { useState } from "react";

/**
 * A hook that returns a confirm function to open a confirmation modal,
 * and a ConfirmationModal component that you can render at the root of your app.
 *
 * Usage:
 * const { confirm, ConfirmationModal } = useConfirmationDialog();
 *
 * Then call:
 * await confirm("Are you sure?");
 * // if confirmed, the promise resolves; if cancelled, it rejects.
 */
const useConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [promiseInfo, setPromiseInfo] = useState(null);

  const confirm = (message) => {
    setIsOpen(true);
    return new Promise((resolve, reject) => {
      setPromiseInfo({ resolve, reject, message });
    });
  };

  const handleConfirm = () => {
    setIsOpen(false);
    if (promiseInfo?.resolve) {
      promiseInfo.resolve(true);
    }
    setPromiseInfo(null);
  };

  const handleCancel = () => {
    setIsOpen(false);
    if (promiseInfo?.reject) {
      promiseInfo.reject(new Error("Cancelled"));
    }
    setPromiseInfo(null);
  };

  // This is the reusable modal component.
  const ConfirmationModal = () => {
    if (!isOpen || !promiseInfo) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-secondary rounded-[32px] shadow-lg w-full max-w-sm p-6 flex flex-col gap-2">
          <div className="">
            <h2 className="text-xl text-background font-bold mb-2 font-nunito leading-5">
              Confirmation
            </h2>
            <p className="mb-4 text-background/70 leading-5">
              {promiseInfo.message}
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-xl border border-background/70 font-nunito text-background/90 hover:bg-background/10"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 rounded-xl bg-primary/90 text-secondary font-nunito hover:bg-primary font-semibold"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  return { confirm, ConfirmationModal };
};

export default useConfirmationDialog;
